export type TaskStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'canceled'
export type TaskStage =
  'queued' | 'downloading' | 'transcribing' | 'correcting' | 'completed' | 'failed' | 'canceled'
export type OrderStatus = 'pending' | 'paid' | 'canceled' | 'refunded'
export type PaymentChannel = 'alipay' | 'wechat'
export type ApiKeyScope = 'transcription:read' | 'transcription:write' | 'credits:read'
export type LedgerType =
  '注册赠送' | '购买' | '任务冻结' | '结算' | '失败退回' | '后台调整' | '退款'
export const SOURCE_PLATFORMS = [
  'douyin',
  'bilibili',
  'xiaohongshu',
  'youtube',
  'wechat_channels',
] as const
export type SourcePlatform = (typeof SOURCE_PLATFORMS)[number]

export const PLATFORM_SELECTIONS = ['auto', ...SOURCE_PLATFORMS] as const
export type PlatformSelection = (typeof PLATFORM_SELECTIONS)[number]

export const ACCOUNT_BATCH_SOURCE_TYPES = [
  'profile',
  'channel',
  'collection',
  'playlist',
  'season',
] as const
export type AccountBatchSourceType = (typeof ACCOUNT_BATCH_SOURCE_TYPES)[number]

export const EXTRACTION_MODES = ['video', ...ACCOUNT_BATCH_SOURCE_TYPES] as const
export type ExtractionMode = (typeof EXTRACTION_MODES)[number]
export type AccountBatchChildStatus =
  | 'discovered'
  | 'submitting'
  | 'queued'
  | 'processing'
  | 'paused'
  | 'completed'
  | 'failed'
export type AccountBatchStatus =
  | 'collecting'
  | 'processing'
  | 'pausing'
  | 'paused'
  | 'completed'
  | 'partial'
  | 'failed'
export type AccountBatchCollectionStopReason =
  | 'end_reached'
  | 'max_items'
  | 'timeout'
  | 'access_denied'
  | 'unavailable'
export type AccountBatchPauseReason = 'user' | 'insufficient_credits'

export interface PageResult<T> {
  items: T[]
  page: number
  pageSize: number
  total: number
}

export interface DurationPackage {
  id: string
  code: string
  name: string
  minutes: number
  durationSeconds: number
  amountFen: number
  purchaseType: 'one-time'
  tag: string
  enabled: boolean
  sortOrder: number
  version?: number
}

export interface TranscriptSegment {
  sequenceNo: number
  startMs: number
  endMs: number
  roleId: string
  label: string
  text: string
  confidence: number | null
}

export interface TranscriptTask {
  id: string
  sourcePlatform: SourcePlatform
  title: string
  excerpt: string
  sourceUrl: string
  status: TaskStatus
  stage: TaskStage
  progressPercent: number
  createdAt: string
  updatedAt: string
  convertedAt: string | null
  durationMs: number
  billableSeconds: number
  chargedSeconds: number
  orderId?: string
  transcript: string
  segments: TranscriptSegment[]
  timeline: Array<[string, string, string]>
  error?: string
  errorCode?: string
  retryOfTaskId?: string | null
}

export interface TaskQuote {
  quoteToken: string
  sourcePlatform: SourcePlatform
  title: string
  durationMs: number
  billableSeconds: number
  availableSeconds: number
  sufficient: boolean
  expiresAt: string
}

export interface DeveloperApiKey {
  id: string
  name: string
  displayPrefix: string
  lastFour: string
  scopes: ApiKeyScope[]
  status: 'active' | 'revoked'
  expiresAt: string | null
  lastUsedAt: string | null
  createdAt: string
  revokedAt: string | null
}

export interface CreatedDeveloperApiKey extends DeveloperApiKey {
  apiKey: string
}

export interface CliDeviceAuthorization {
  userCode: string
  clientName: string
  scopes: ApiKeyScope[]
  status: 'pending' | 'approved' | 'consumed' | 'denied' | 'expired'
  expiresAt: string
}

export interface Order {
  id: string
  orderNo: string
  userId?: string
  username?: string
  phone?: string
  packId?: string
  packCode: string
  packName: string
  minutes: number
  amountFen: number
  channel: PaymentChannel
  status: OrderStatus
  createdAt: string
  updatedAt: string
  expiresAt: string
  paidAt: string | null
  canceledAt: string | null
  credited: boolean
  refund: { amountFen: number; completedAt: string } | null
}

export interface CreditAccount {
  availableSeconds: number
  frozenSeconds: number
  consumedSeconds: number
  totalSeconds: number
  updatedAt: string
}

export interface LedgerRecord {
  id: string
  type: LedgerType
  entryType: string
  referenceType: string
  deltaSeconds: number
  createdAt: string
  reference: string
}

export interface AccountBatchChild {
  sequenceNo: number
  videoId: string
  taskId: string | null
  title: string
  status: AccountBatchChildStatus
  errorCode: string | null
  errorMessage: string | null
}

export interface AccountBatch {
  batchId: string
  sourcePlatform: SourcePlatform
  sourceType: AccountBatchSourceType
  maxItems: number
  status: AccountBatchStatus
  collectionComplete: boolean
  collectionStopReason: AccountBatchCollectionStopReason | null
  pauseReason: AccountBatchPauseReason | null
  hasMore: boolean
  total: number
  discovered: number
  submitting: number
  queued: number
  processing: number
  paused: number
  completed: number
  failed: number
  items: AccountBatchChild[]
}

export interface RecentAccountBatch extends AccountBatch {
  createdAt: string
  sourceUrl: string
}
